import {
  BigQuerySQLConnectionAuthMethod,
  CustomConnectionAuthMethod,
  HMACHashAlgorithm,
  HMACSignatureEncoding,
  HMACSignatureLocation,
  InboundWebhookConnectionAuthMethod,
  InboundWebhookResource,
  IntegrationProvider as IntegrationProviderBase,
  MongoDBConnectionAuthMethod,
  MySQLConnectionAuthMethod,
  PostgreSQLConnectionAuthMethod,
  Provider,
  RedshiftConnectionAuthMethod,
  RedshiftSSLMode,
  ResourceConfig,
  ResourceTypesForIntegrationProvider,
  SnowflakeConnectionAuthMethod,
  TLSMode,
} from "src/api/connectApi/types";
import {
  Clause,
  DataRetentionUnit as DataRetentionUnitEnum,
  Junctions,
} from "src/clients/flow-api";

export type ConnectionAuthNoAuth = {
  auth_method: "no_auth";
};

export type ConnectionAuthAPIKey = {
  auth_method: "api_key";
  header?: string;
  prefix?: string;
  api_key_as_query_param?: boolean;
  query_param_name?: string;
};
export type ConnectionAuthAWSSignature = {
  auth_method: "aws_signature";
  aws_access_key: string;
  aws_host: string;
  aws_region: string;
  aws_service: string;
};

export type KeyValuePair = {
  key: string;
  value: string | null;
  secret?: boolean;
  required?: boolean;
  environment?: string | null;
  tenant_id?: string | null;
};

export type ConnectionSSLConfigValue = {
  value: string | null;
  secret?: boolean;
};

export type ConnectionSSLConfig = {
  ssl_certificate: ConnectionSSLConfigValue;
  ssl_key: ConnectionSSLConfigValue;
};

export type ConnectionAuthOAuth2 = {
  auth_method: "oauth2";
  uses_basic_auth: boolean;
  basic_auth_header?: string;
  basic_auth_prefix?: string;
  authorize_url?: string;
  authorize_query_params?: KeyValuePair[];
  authorize_headers?: KeyValuePair[];
  authorize_body?: string;
  authorize_body_token_path?: string;
  authorize_body_expires_path?: string;
  scopes?: string;
  scope_delimiter?: string;
  header?: string;
  prefix?: string;
  // always client_credentials
  grant_type?: "client_credentials";

  // Refresh properties
  refresh_url?: string;
  refresh_query_params?: KeyValuePair[];
  refresh_headers?: KeyValuePair[];
  refresh_body?: string;
  token_refresh_interval?: number;
};
export type ConnectionAuthBasicAuth = {
  auth_method: "basic";
};

export type InboundWebhookConnectionAuthNoAuth = object;

export type InboundWebhookConnectionAuthBasicAuth = {
  login: KeyValuePair; // secret
  password: KeyValuePair; // secret
};

export type InboundWebhookConnectionAuthAPIKey = {
  header?: string | null;
  prefix?: string | null;
  apiKey: KeyValuePair; // secret
};

export type InboundWebhookConnectionAuthHMAC = {
  hashAlgorithm: string;
  signatureEncoding: string;
  header: string;
  signingKey: KeyValuePair; // secret
};

export type InboundWebhookConnectionAuthHMACPersona = {
  signingKey: KeyValuePair; // secret
};

export type InboundWebhookConnectionAuthHMACArva = {
  signingKey: KeyValuePair; // secret
};

export type InboundWebhookConnectionAuthHMACSyntage = {
  signingKey: KeyValuePair; // secret
};

export type InboundWebhookConnectionAuthHMACHandshake = {
  signingKey: KeyValuePair; // secret
};

export type PostgreSQLConnectionAuthUsernamePassword = {
  username: KeyValuePair; // secret
  password: KeyValuePair; // secret
};

export type MySQLConnectionAuthUsernamePassword = {
  username: KeyValuePair; // secret
  password: KeyValuePair; // secret
};

export type BigQuerySQLConnectionAuthServiceAccount = {
  service_account_key: KeyValuePair; // secret
};

export type SnowflakeConnectionBasicAuth = {
  basicAuthUsername: KeyValuePair; // secret
  password: KeyValuePair; // secret
};

export type SnowflakeConnectionKeyPairAuth = {
  username: KeyValuePair; // secret
  privateKey: KeyValuePair; // secret
  privateKeyPassphrase: KeyValuePair; // secret
};

export type RedshiftConnectionUsernamePasswordAuth = {
  username: KeyValuePair; // secret
  password: KeyValuePair; // secret
};

export type RedshiftConnectionAwsIamAuth = {
  databaseUsername: KeyValuePair; // secret
  awsAccessKeyId: KeyValuePair; // secret
  awsSecretKey: KeyValuePair; // secret
};

export type MongoDBConnectionUsernamePasswordAuth = {
  username: KeyValuePair; // secret
  password: KeyValuePair; // secret
};

export type ConnectionAuthGoogleOAuth2 = {
  auth_method: "google_oauth2";
  authorize_url: string;
  scope: string;
  filename?: string;
  token_refresh_interval?: number;
};
export type ConnectionAuthCustomCode = {
  auth_method: "custom_code";
  auth_code: string;
};
export type ConnectionAuthHMAC = {
  auth_method: "hmac";
  hash_algorithm: HMACHashAlgorithm;
  signature_encoding: HMACSignatureEncoding;
  signature_prefix?: string;
  signature_location: HMACSignatureLocation;
  signature_field_name: string;
};
export type ConnectionConfigAuthMethods = {
  authMethod: CustomConnectionAuthMethod;
  // React hook form doesn't support union types: https://github.com/react-hook-form/react-hook-form/issues/9287
  noAuthConfig: ConnectionAuthNoAuth;
  basicAuthConfig: ConnectionAuthBasicAuth;
  oauth2Config: ConnectionAuthOAuth2;
  apiKeyConfig: ConnectionAuthAPIKey;
  awsSignatureConfig: ConnectionAuthAWSSignature;
  googleOAuth2Config: ConnectionAuthGoogleOAuth2;
  customCodeConfig: ConnectionAuthCustomCode;
  hmacConfig: ConnectionAuthHMAC;
};

export type ConnectionConfig = ConnectionConfigAuthMethods & {
  url: string;
  probeUrl: string;
  secrets?: KeyValuePair[];
  headers: KeyValuePair[];
  allowTestInvocations: boolean;

  enableSSL: boolean;
  sslConfig?: ConnectionSSLConfig;
  hasSSHTunnelEnabled: boolean;
  SSHTunnel?: DBConnectionSSHTunnel;
};

export type DataRetentionUnit = `${DataRetentionUnitEnum}`;

export type DataRetention = {
  value: number;
  unit: DataRetentionUnit;
};

export type ConnectionConfigInputs = ConnectionConfig & {
  name: string;
  mediaKey?: string | null;
  hasRawProviderResponseEnabled: boolean;
  hasRawProviderRequestEnabled: boolean;
  dataRetentionDays: number;
  enableNonProdConfigs: boolean;
  dataRetention: DataRetention;

  nonProdEnvConfigs?: Record<string, ConnectionConfig>;
};

export type InboundWebhookConnectionConfig = {
  webhookAuthMethod: InboundWebhookConnectionAuthMethod;
  webhookNoAuthConfig: InboundWebhookConnectionAuthNoAuth;
  webhookBasicAuthConfig: InboundWebhookConnectionAuthBasicAuth;
  webhookApiKeyConfig: InboundWebhookConnectionAuthAPIKey;
  webhookHMACConfig: InboundWebhookConnectionAuthHMAC;
  webhookHMACPersonaConfig: InboundWebhookConnectionAuthHMACPersona;
  webhookHMACArvaConfig: InboundWebhookConnectionAuthHMACArva;
  webhookHMACSyntageConfig: InboundWebhookConnectionAuthHMACSyntage;
  webhookTruliooHMACHandshakeConfig: InboundWebhookConnectionAuthHMACHandshake;
  webhookURL: string | null;
  allowTestInvocations: boolean;
};

export type InboundWebhookResourceConfig = {
  id: string;
  name: string;
  resource: InboundWebhookResource;
  correlationIDPath: string;
  activation: {
    junction: Junctions;
    clauses: Clause[];
  } | null;
};

export type InboundWebhookConnectionConfigInputs = {
  name: string;
  mediaKey?: string | null;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: InboundWebhookConnectionConfig;
  sandboxConfig: InboundWebhookConnectionConfig | null;
  resourceConfigs: InboundWebhookResourceConfig[];
  customSuccessStatusCode: number | null;
};

export type MTLSConfig = {
  mode: TLSMode;
  clientCertificate: string;
  clientKey: KeyValuePair; // secret
  CACertificate: string;
};

export type DBConnectionWithSSHTunnelInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: PostgreSQLConnectionConfig;
  sandboxConfig: PostgreSQLConnectionConfig | null;
};

/// DB Connection SSH Tunnel Config
export type DBConnectionSSHTunnel = {
  host: string;
  port: number;
  user: string;
  privateKey: KeyValuePair;
};

export type DBSSHTunnelInputs = {
  SSHTunnel?: DBConnectionSSHTunnel;
  hasSSHTunnelEnabled: boolean;
};

export type DBMTLSInputs = {
  hasMTLSEnabled: boolean;
  mTLSConfig: MTLSConfig;
};

export type PostgreSQLConnectionConfig = {
  databaseName: string;
  host: string;
  port?: number | null;
  authMethod: PostgreSQLConnectionAuthMethod;
  usernamePasswordConfig: PostgreSQLConnectionAuthUsernamePassword;
  allowTestInvocations: boolean;
} & DBSSHTunnelInputs &
  DBMTLSInputs;

// This alias already is used in many places, keep it as it is
// eslint-disable-next-line sonarjs/redundant-type-aliases
export type PostgreSQLConnectionConfigInputs = DBConnectionWithSSHTunnelInputs;

export interface CockroachDBRetryConfig {
  maxRetries: number;
  maxBackoff: number | undefined;
}

export type CockroachDBConnectionConfig = {
  hasRetryEnabled: boolean;
  retryConfig?: CockroachDBRetryConfig;
} & PostgreSQLConnectionConfig;

export type CockroachDBConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: CockroachDBConnectionConfig;
  sandboxConfig: CockroachDBConnectionConfig | null;
};

export type MySQLConnectionConfig = {
  databaseName: string;
  host: string;
  port?: number | null;
  authMethod: MySQLConnectionAuthMethod;
  usernamePasswordConfig: MySQLConnectionAuthUsernamePassword;
  allowTestInvocations: boolean;
} & DBSSHTunnelInputs &
  DBMTLSInputs;

export type MySQLConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: MySQLConnectionConfig;
  sandboxConfig: MySQLConnectionConfig | null;
};

export type Environment = "production" | "sandbox";

export type PostgreSQLConnectionAuthConfigField =
  `usernamePasswordConfig.${keyof PostgreSQLConnectionConfig["usernamePasswordConfig"]}`;

export type CockroachDBConnectionAuthConfigField =
  `usernamePasswordConfig.${keyof CockroachDBConnectionConfig["usernamePasswordConfig"]}`;

export type MySQLConnectionAuthConfigField =
  `usernamePasswordConfig.${keyof MySQLConnectionConfig["usernamePasswordConfig"]}`;

export type BigQuerySQLConnectionConfig = {
  scopes: string[];
  authMethod: BigQuerySQLConnectionAuthMethod;
  serviceAccountConfig: BigQuerySQLConnectionAuthServiceAccount;
  allowTestInvocations: boolean;
};

export type BigQuerySQLConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: BigQuerySQLConnectionConfig;
  sandboxConfig: BigQuerySQLConnectionConfig | null;
};

export type SnowflakeConnectionConfig = {
  accountIdentifier: string;
  warehouseName: string;
  databaseName: string;
  databaseSchema: string | null;
  userRole: string | null;
  authMethod: SnowflakeConnectionAuthMethod;
  basicAuthConfig: SnowflakeConnectionBasicAuth;
  keyPairAuthConfig: SnowflakeConnectionKeyPairAuth;
  allowTestInvocations: boolean;
};

export type SnowflakeConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: SnowflakeConnectionConfig;
  sandboxConfig: SnowflakeConnectionConfig | null;
};

export type SnowflakeConnectionAuthConfigField =
  | `basicAuthConfig.${keyof SnowflakeConnectionConfig["basicAuthConfig"]}`
  | `keyPairAuthConfig.${keyof SnowflakeConnectionConfig["keyPairAuthConfig"]}`;

export type RedshiftConnectionSslConfig = {
  mode: RedshiftSSLMode;
};

export type RedshiftConnectionConfig = {
  host?: string | null;
  port?: number | null;
  databaseName?: string | null;
  clusterIdentifier?: string | null;
  awsRegion?: string | null;
  roleArn?: string | null;
  hasSslEnabled: boolean;
  SSHTunnel?: DBConnectionSSHTunnel;
  hasSSHTunnelEnabled: boolean;
  authMethod: RedshiftConnectionAuthMethod;
  usernamePasswordConfig: RedshiftConnectionUsernamePasswordAuth;
  awsIamConfig: RedshiftConnectionAwsIamAuth;
  allowTestInvocations: boolean;
};

// MongoDB Connection Config
export type MongoDBConnectionConfig = {
  databaseName: string;
  host?: string;
  nonSrvHostString?: string;
  authSource?: string;
  allowTestInvocations: boolean;
  authMethod: MongoDBConnectionAuthMethod;
  usernamePasswordConfig: MongoDBConnectionUsernamePasswordAuth;
};

export type MongoDBConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: MongoDBConnectionConfig;
  sandboxConfig: MongoDBConnectionConfig | null;
};

export type RedshiftConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: RedshiftConnectionConfig;
  sandboxConfig: RedshiftConnectionConfig | null;
};

export type RedshiftConnectionAuthConfigField =
  | `usernamePasswordConfig.${keyof RedshiftConnectionConfig["usernamePasswordConfig"]}`
  | `awsIamConfig.${keyof RedshiftConnectionConfig["awsIamConfig"]}`;

export type ProviderSelectInputs = {
  provider?: Provider | string;
  isManifestProvider: boolean;
};

export type MongoDBConnectionAuthConfigField =
  `usernamePasswordConfig.${keyof MongoDBConnectionConfig["usernamePasswordConfig"]}`;

export type ConnectionInputs = {
  selectProviderForm: ProviderSelectInputs;
  configConnectionForm: ConnectionConfigInputs;
};

// Undefined secret type maps to a basic text input
type ConnectionSecrets = "multiline" | null;

export type ConnectionTemplate<
  IntegrationProvider extends IntegrationProviderBase,
> = {
  provider: IntegrationProvider;
  secrets: {
    key: string;
    name: string;
    hint?: string;
    required?: boolean;
    type?: ConnectionSecrets;
  }[];
  resources: Record<
    ResourceTypesForIntegrationProvider<IntegrationProvider>,
    ConnectionResourceTemplate
  >;
};

export type ConnectionResourceTemplate = {
  name: string;
  deprecated?: boolean;
  configKeys: ConnectionResourceTemplateConfigKey[];
  hint?: string;
};

export type ConnectionResourceTemplateConfigKeyParent = {
  key: string;
  title: string;
  subtitle?: string;
  required?: boolean;
};

export type ConnectionResourceTemplateConfigKey = {
  key: string;
  name: string;
  required: boolean;
  hint?: string;
  placeholder?: string;
  parent?: ConnectionResourceTemplateConfigKeyParent;
  // Callback to hide optional configuration fields based on
  // values of other fields
  isHidden?: (config: ResourceConfig) => boolean;
} & (
  | {
      type: "text";
    }
  | {
      type: "number";
    }
  | {
      type: "inline_number";
    }
  | {
      type: "simple_selection";
      elements: { key: string | number | undefined; value: string }[];
    }
  | {
      type: "multiple_selection";
      elements: { key: string | number; value: string }[];
      minItems?: number;
      maxItems?: number;
    }
  | {
      type: "switch";
    }
);

// Needed to make TypeScript infer the generic
// parameter based on the provider field
export const makeConnectionTemplate = <
  IntegrationProvider extends IntegrationProviderBase,
>(
  template: ConnectionTemplate<IntegrationProvider>,
): ConnectionTemplate<IntegrationProvider> => template;

// This should be changed to support any string
// instead of just "sandbox" once we support multiple environments
export type AvailableEnvironmentPrefixes = "nonProdEnvConfigs.sandbox." | "";

export type ConnectionCreationFieldError = {
  loc: string | null;
  msg: string;
};

export type InboundWebhookValidationError = {
  loc: (string | number)[];
  msg: string;
  type?: string;
  ctx?: {
    msg?: string;
    field_errors?: ConnectionCreationFieldError[];
  };
};

export type InboundWebhookValidationErrorResponse = {
  detail?: InboundWebhookValidationError[];
};

export type RetoolConnectionConfig = {
  url: string;
  appUUID: string;
  groupID: string;
  apiKey: KeyValuePair;
};

export type RetoolConnectionConfigInputs = {
  name: string;
  dataRetention: DataRetention;
  enableNonProdConfigs: boolean;

  productionConfig: RetoolConnectionConfig;
  sandboxConfig: RetoolConnectionConfig | null;
};
